import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";
import type { EmergencyButtonConfig, EmergencyButtonPosition } from "./types";

const DEFAULT_CONFIG: Omit<EmergencyButtonConfig, "user_id" | "updated_at"> = {
  enabled: true,
  label: "Emergency",
  icon: "shield-alert",
  color_hsl: "0 84% 60%",
  position: "bottom-right",
  show_on_lock: false,
};

export function useEmergencyConfig() {
  const { user } = useAuth();
  const [config, setConfig] = useState<EmergencyButtonConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setConfig(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data } = await (supabase as any)
        .from("emergency_button_config")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled) return;
      if (data) {
        setConfig(data as EmergencyButtonConfig);
      } else {
        setConfig({
          ...DEFAULT_CONFIG,
          user_id: user.id,
          updated_at: new Date().toISOString(),
        });
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const update = useCallback(
    async (patch: Partial<Omit<EmergencyButtonConfig, "user_id" | "updated_at">>) => {
      if (!user) return { error: "Not signed in." };
      const next = {
        ...(config ?? { ...DEFAULT_CONFIG, user_id: user.id, updated_at: new Date().toISOString() }),
        ...patch,
        user_id: user.id,
        updated_at: new Date().toISOString(),
      };
      setConfig(next);
      const { error } = await (supabase as any)
        .from("emergency_button_config")
        .upsert(next, { onConflict: "user_id" });
      if (error) return { error: error.message };
      return { error: null };
    },
    [user, config],
  );

  return { config, loading, update };
}

export const EMERGENCY_POSITIONS: { value: EmergencyButtonPosition; label: string }[] = [
  { value: "bottom-right", label: "Bottom right" },
  { value: "bottom-left", label: "Bottom left" },
  { value: "top-right", label: "Top right" },
  { value: "top-left", label: "Top left" },
];
